import { 
  collection, 
  doc, 
  addDoc, 
  getDocs, 
  getDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';

// Collection name for discounts
const DISCOUNTS_COLLECTION = 'discounts';

/**
 * Create a new discount code
 * @param {Object} discountData - The discount data
 * @param {string} discountData.sellerId - The seller's user ID
 * @param {string} discountData.code - The discount code (e.g., SUMMER20)
 * @param {string} discountData.type - The discount type ('percentage' or 'fixed')
 * @param {number} discountData.amount - The discount amount
 * @param {number} discountData.minSubtotal - Minimum order subtotal (optional)
 * @param {string} discountData.scope - The discount scope ('entire_order', 'specific_products', 'specific_categories')
 * @param {Array} discountData.productIds - Array of product IDs if scope is 'specific_products'
 * @param {Array} discountData.categoryIds - Array of category IDs if scope is 'specific_categories'
 * @param {string} discountData.status - The discount status ('active', 'inactive', 'expired')
 * @returns {Promise<Object>} The created discount document
 */
export const createDiscount = async (discountData) => {
  try {
    // Validate required fields
    if (!discountData.sellerId || !discountData.code || !discountData.type || !discountData.amount) {
      throw new Error('Missing required fields: sellerId, code, type, and amount are required');
    }

    // Validate discount type and amount
    if (discountData.type === 'percentage' && (discountData.amount < 1 || discountData.amount > 100)) {
      throw new Error('Percentage discount must be between 1 and 100');
    }

    if (discountData.type === 'fixed' && discountData.amount <= 0) {
      throw new Error('Fixed discount amount must be greater than 0');
    }

    // Check if code already exists for this seller
    const existingDiscount = await getDiscountByCode(discountData.sellerId, discountData.code);
    if (existingDiscount) {
      throw new Error('Discount code already exists for this seller');
    }

    // Prepare the discount document
    const discountDoc = {
      ...discountData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      usageCount: 0,
      totalDiscountAmount: 0
    };

    // Add to Firestore
    const docRef = await addDoc(collection(db, DISCOUNTS_COLLECTION), discountDoc);
    
    return {
      id: docRef.id,
      ...discountDoc
    };
  } catch (error) {
    console.error('Error creating discount:', error);
    throw error;
  }
};

/**
 * Get all discounts for a seller
 * @param {string} sellerId - The seller's user ID
 * @returns {Promise<Array>} Array of discount documents
 */
export const getDiscountsBySeller = async (sellerId) => {
  try {
    // First try with ordering (requires index)
    try {
      const q = query(
        collection(db, DISCOUNTS_COLLECTION),
        where('sellerId', '==', sellerId),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const discounts = [];
      
      querySnapshot.forEach((doc) => {
        discounts.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      return discounts;
    } catch (indexError) {
      // If index isn't ready, fall back to simple query and sort client-side
      console.log('Index not ready, falling back to client-side sorting:', indexError.message);
      
      const q = query(
        collection(db, DISCOUNTS_COLLECTION),
        where('sellerId', '==', sellerId)
      );
      
      const querySnapshot = await getDocs(q);
      const discounts = [];
      
      querySnapshot.forEach((doc) => {
        discounts.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      // Sort client-side by createdAt (newest first)
      return discounts.sort((a, b) => {
        const aTime = a.createdAt?.toDate?.() || a.createdAt || new Date(0);
        const bTime = b.createdAt?.toDate?.() || b.createdAt || new Date(0);
        return bTime - aTime;
      });
    }
  } catch (error) {
    console.error('Error getting discounts by seller:', error);
    throw error;
  }
};

/**
 * Get a specific discount by ID
 * @param {string} discountId - The discount document ID
 * @returns {Promise<Object|null>} The discount document or null if not found
 */
export const getDiscountById = async (discountId) => {
  try {
    const docRef = doc(db, DISCOUNTS_COLLECTION, discountId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error getting discount by ID:', error);
    throw error;
  }
};

/**
 * Get a discount by code for a specific seller
 * @param {string} sellerId - The seller's user ID
 * @param {string} code - The discount code
 * @returns {Promise<Object|null>} The discount document or null if not found
 */
export const getDiscountByCode = async (sellerId, code) => {
  try {
    const q = query(
      collection(db, DISCOUNTS_COLLECTION),
      where('sellerId', '==', sellerId),
      where('code', '==', code.toUpperCase())
    );
    
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data()
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error getting discount by code:', error);
    throw error;
  }
};

/**
 * Update a discount
 * @param {string} discountId - The discount document ID
 * @param {Object} updateData - The data to update
 * @returns {Promise<void>}
 */
export const updateDiscount = async (discountId, updateData) => {
  try {
    const docRef = doc(db, DISCOUNTS_COLLECTION, discountId);
    
    // Add updated timestamp
    const updatePayload = {
      ...updateData,
      updatedAt: serverTimestamp()
    };
    
    await updateDoc(docRef, updatePayload);
  } catch (error) {
    console.error('Error updating discount:', error);
    throw error;
  }
};

/**
 * Delete a discount
 * @param {string} discountId - The discount document ID
 * @returns {Promise<void>}
 */
export const deleteDiscount = async (discountId) => {
  try {
    const docRef = doc(db, DISCOUNTS_COLLECTION, discountId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting discount:', error);
    throw error;
  }
};

/**
 * Validate a discount code for checkout
 * @param {string} sellerId - The seller's user ID
 * @param {string} code - The discount code
 * @param {number} orderSubtotal - The order subtotal
 * @param {Array} productIds - Array of product IDs in the order
 * @param {Array} categoryIds - Array of category IDs in the order
 * @returns {Promise<Object>} Validation result with discount data or error
 */
export const validateDiscountCode = async (sellerId, code, orderSubtotal, productIds = [], categoryIds = []) => {
  try {
    const discount = await getDiscountByCode(sellerId, code);
    
    if (!discount) {
      return {
        valid: false,
        error: 'Invalid discount code'
      };
    }
    
    if (discount.status !== 'active') {
      return {
        valid: false,
        error: 'Discount code is not active'
      };
    }
    
    // Check minimum subtotal
    if (discount.minSubtotal && orderSubtotal < discount.minSubtotal) {
      return {
        valid: false,
        error: `Minimum order subtotal of $${discount.minSubtotal} required`
      };
    }
    
    // Check scope restrictions
    if (discount.scope === 'specific_products') {
      const hasValidProduct = productIds.some(id => discount.productIds?.includes(id));
      if (!hasValidProduct) {
        return {
          valid: false,
          error: 'Discount code does not apply to items in your order'
        };
      }
    }
    
    if (discount.scope === 'specific_categories') {
      const hasValidCategory = categoryIds.some(id => discount.categoryIds?.includes(id));
      if (!hasValidCategory) {
        return {
          valid: false,
          error: 'Discount code does not apply to items in your order'
        };
      }
    }
    
    return {
      valid: true,
      discount
    };
  } catch (error) {
    console.error('Error validating discount code:', error);
    return {
      valid: false,
      error: 'Error validating discount code'
    };
  }
};

/**
 * Apply a discount to an order
 * @param {string} discountId - The discount document ID
 * @param {number} orderSubtotal - The order subtotal
 * @returns {Object} The calculated discount amount and final total
 */
export const calculateDiscount = (discount, orderSubtotal) => {
  let discountAmount = 0;
  
  if (discount.type === 'percentage') {
    discountAmount = (orderSubtotal * discount.amount) / 100;
  } else if (discount.type === 'fixed') {
    discountAmount = Math.min(discount.amount, orderSubtotal);
  }
  
  const finalTotal = orderSubtotal - discountAmount;
  
  return {
    discountAmount: Math.round(discountAmount * 100) / 100, // Round to 2 decimal places
    finalTotal: Math.round(finalTotal * 100) / 100
  };
};

/**
 * Generate a random discount code
 * @param {number} length - The length of the code (default: 8)
 * @returns {string} A random discount code
 */
export const generateRandomDiscountCode = (length = 8) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  
  return result;
};

/**
 * Get discount statistics for a seller
 * @param {string} sellerId - The seller's user ID
 * @returns {Promise<Object>} Discount statistics
 */
export const getDiscountStats = async (sellerId) => {
  try {
    const discounts = await getDiscountsBySeller(sellerId);
    
    const stats = {
      total: discounts.length,
      active: discounts.filter(d => d.status === 'active').length,
      inactive: discounts.filter(d => d.status === 'inactive').length,
      expired: discounts.filter(d => d.status === 'expired').length,
      totalUsage: discounts.reduce((sum, d) => sum + (d.usageCount || 0), 0),
      totalSavings: discounts.reduce((sum, d) => sum + (d.totalDiscountAmount || 0), 0)
    };
    
    return stats;
  } catch (error) {
    console.error('Error getting discount stats:', error);
    throw error;
  }
};
